import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/base/SEO';

export default function CookiePolicy() {
  const lastUpdated = 'January 11, 2025';

  const cookieTypes = [
    {
      title: 'Strictly Necessary Cookies',
      icon: 'ri-shield-check-line',
      color: 'from-red-500 to-red-600',
      description: 'These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and accessibility.',
      canDisable: false,
      examples: [
        { name: 'session_id', purpose: 'Maintains your session state', duration: 'Session' },
        { name: 'csrf_token', purpose: 'Protects against cross-site request forgery', duration: '1 hour' },
        { name: 'auth_token', purpose: 'Authenticates your login', duration: '30 days' },
      ],
    },
    {
      title: 'Performance Cookies',
      icon: 'ri-line-chart-line',
      color: 'from-blue-500 to-blue-600',
      description: 'These cookies collect information about how you use our website, such as which pages you visit most often. This helps us improve how our website works.',
      canDisable: true,
      examples: [
        { name: '_ga', purpose: 'Google Analytics - tracks user behavior', duration: '2 years' },
        { name: '_gid', purpose: 'Google Analytics - identifies unique users', duration: '24 hours' },
        { name: 'performance_metrics', purpose: 'Collects page load times and errors', duration: '1 year' },
      ],
    },
    {
      title: 'Functional Cookies',
      icon: 'ri-settings-3-line',
      color: 'from-green-500 to-green-600',
      description: 'These cookies allow the website to remember choices you make and provide enhanced, more personalized features.',
      canDisable: true,
      examples: [
        { name: 'theme_preference', purpose: 'Remembers your theme choice (light/dark)', duration: '1 year' },
        { name: 'language', purpose: 'Stores your language preference', duration: '1 year' },
        { name: 'dashboard_layout', purpose: 'Saves your dashboard customization', duration: '6 months' },
      ],
    },
    {
      title: 'Targeting/Advertising Cookies',
      icon: 'ri-advertisement-line',
      color: 'from-purple-500 to-purple-600',
      description: 'These cookies are used to deliver advertisements more relevant to you and your interests. They also help limit the number of times you see an advertisement.',
      canDisable: true,
      examples: [
        { name: '_fbp', purpose: 'Facebook Pixel - tracks conversions', duration: '3 months' },
        { name: 'ads_preference', purpose: 'Stores advertising preferences', duration: '1 year' },
        { name: 'conversion_tracking', purpose: 'Measures ad campaign effectiveness', duration: '90 days' },
      ],
    },
  ];

  const thirdPartyServices = [
    {
      name: 'Google Analytics',
      purpose: 'Website analytics and performance tracking',
      link: 'https://policies.google.com/privacy',
    },
    {
      name: 'Stripe',
      purpose: 'Payment processing',
      link: 'https://stripe.com/privacy',
    },
    {
      name: 'Intercom',
      purpose: 'Customer support and messaging',
      link: 'https://www.intercom.com/legal/privacy',
    },
    {
      name: 'Facebook Pixel',
      purpose: 'Advertising and conversion tracking',
      link: 'https://www.facebook.com/privacy/policy',
    },
  ];

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <SEO
        title="Cookie Policy - MandaPost"
        description="Learn about how MandaPost uses cookies to enhance your experience, improve our services, and provide personalized content."
        keywords="cookie policy, cookies, privacy, data collection, tracking"
        canonicalUrl="https://mandapost.com/cookie-policy"
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full text-white font-medium mb-6">
              <i className="ri-file-text-line mr-2"></i>
              Legal Information
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Cookie Policy
            </h1>

            <p className="text-xl lg:text-2xl text-white/90 mb-6 leading-relaxed">
              Transparency about how we use cookies to improve your experience
            </p>

            <p className="text-white/80">
              Last Updated: {lastUpdated}
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-2xl border-2 border-[#66b3ff]/30 p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-white text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#22577A] mb-4">What Are Cookies?</h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide a better user experience.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  At MandaPost, we use cookies to understand how you use our platform, remember your preferences, and improve our services. This policy explains what cookies we use, why we use them, and how you can control them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Cookies */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-[#e4f4ff]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              Types of Cookies We Use
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We use different types of cookies for various purposes to enhance your experience
            </p>
          </div>

          <div className="space-y-8">
            {cookieTypes.map((type, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden hover:border-[#66b3ff] hover:shadow-xl transition-all"
              >
                <div className={`bg-gradient-to-r ${type.color} px-8 py-6 flex items-center justify-between`}>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-lg flex items-center justify-center">
                      <i className={`${type.icon} text-white text-2xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">{type.title}</h3>
                      <p className="text-white/90 mt-1">{type.description}</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {type.canDisable ? (
                      <span className="px-4 py-2 bg-white/20 rounded-full text-white text-sm font-semibold">
                        Optional
                      </span>
                    ) : (
                      <span className="px-4 py-2 bg-white rounded-full text-[#22577A] text-sm font-semibold">
                        Required
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-8">
                  <h4 className="font-bold text-[#22577A] mb-4">Examples:</h4>
                  <div className="space-y-3">
                    {type.examples.map((example, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="font-semibold text-[#22577A] mb-1">
                              <code className="text-sm bg-gray-200 px-2 py-1 rounded">{example.name}</code>
                            </div>
                            <p className="text-gray-700 text-sm">{example.purpose}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs text-gray-500 uppercase font-semibold">Duration</div>
                            <div className="text-sm font-semibold text-[#22577A]">{example.duration}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Third-Party Cookies */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              Third-Party Services
            </h2>
            <p className="text-xl text-gray-600">
              We work with trusted partners who may also set cookies on our website
            </p>
          </div>

          <div className="space-y-4">
            {thirdPartyServices.map((service, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-[#e4f4ff]/10 rounded-xl p-6 border-2 border-gray-200 hover:border-[#66b3ff] transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-[#22577A] mb-2">{service.name}</h3>
                    <p className="text-gray-700">{service.purpose}</p>
                  </div>
                  <a
                    href={service.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center gap-2"
                  >
                    Privacy Policy
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cookie Management */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-[#e4f4ff]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              Managing Your Cookie Preferences
            </h2>
            <p className="text-xl text-gray-600">
              You have control over which cookies you accept
            </p>
          </div>

          <div className="bg-white rounded-2xl border-2 border-gray-200 p-8">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-settings-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#22577A] mb-2">Browser Settings</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Most web browsers allow you to control cookies through their settings. You can set your browser to refuse cookies or delete certain cookies. However, please note that if you block or delete cookies, some features of our website may not work properly.
                  </p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <a
                      href="https://support.google.com/chrome/answer/95647"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      <i className="ri-chrome-line text-[#22577A]"></i>
                      Chrome Settings
                      <i className="ri-external-link-line text-xs ml-auto"></i>
                    </a>
                    <a
                      href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      <i className="ri-firefox-line text-[#22577A]"></i>
                      Firefox Settings
                      <i className="ri-external-link-line text-xs ml-auto"></i>
                    </a>
                    <a
                      href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      <i className="ri-safari-line text-[#22577A]"></i>
                      Safari Settings
                      <i className="ri-external-link-line text-xs ml-auto"></i>
                    </a>
                    <a
                      href="https://support.microsoft.com/en-us/windows/manage-cookies-in-microsoft-edge-view-allow-block-delete-and-use-168dab11-0753-043d-7c16-ede5947fc64d"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      <i className="ri-edge-line text-[#22577A]"></i>
                      Edge Settings
                      <i className="ri-external-link-line text-xs ml-auto"></i>
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#66b3ff] to-[#b3d9ff] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-toggle-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#22577A] mb-2">Cookie Consent Manager</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    When you first visit MandaPost, you'll see a cookie consent banner where you can choose which types of cookies to accept. You can change your preferences at any time by clicking the "Cookie Settings" link in the footer.
                  </p>
                  <button className="px-6 py-3 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center gap-2">
                    <i className="ri-settings-3-line"></i>
                    Manage Cookie Preferences
                  </button>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#38a3a5] to-[#66b3ff] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#22577A] mb-2">Do Not Track</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We respect Do Not Track (DNT) signals. If you enable DNT in your browser, we will not use cookies for tracking or advertising purposes, though we may still use strictly necessary cookies for the website to function.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Updates to Policy */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-2xl border-2 border-[#66b3ff]/30 p-8">
            <h2 className="text-2xl font-bold text-[#22577A] mb-4">Changes to This Policy</h2>
            <p className="text-gray-700 mb-4 leading-relaxed">
              We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new Cookie Policy on this page and updating the "Last Updated" date.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We encourage you to review this Cookie Policy periodically to stay informed about how we use cookies.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-mail-line text-white text-3xl"></i>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Questions About Our Cookie Policy?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              If you have any questions about how we use cookies or this policy, please don't hesitate to contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all"
              >
                <i className="ri-message-3-line"></i>
                Contact Us
              </a>
              <a
                href="/privacy-policy"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold text-lg hover:bg-white/10 transition-all"
              >
                <i className="ri-shield-user-line"></i>
                View Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
